import { type ZodError, type ZodSafeParseResult, z } from "zod/v4";

import { type JsonPathLiteral, type Store } from "@moment/api-collab/differential/index";
import { jsonPathToMergePatchPath } from "@moment/api-collab/differential/jsonTable/jsonPathToMergePatchPath";
import { pluck } from "@moment/api-collab/differential/jsonTable/pluck";
import { type TopLevelQuerySpec } from "@moment/api-collab/differential/operatorsV2/QuerySpec";
import { type TypeInfo } from "@moment/api-collab/differential/schema/index";
import { type DatabaseTables } from "@moment/api-collab/differential/types";

import { type ColorVariant } from "../Chip";
import { CLEAR_SELECT_ITEM_VALUE } from "../Select";
import { type Reaction, reactionSchema } from "./Reaction";
import { type User, userSchema } from "./User";
import { stringifyValue } from "./stringifyValue";
import { type HydratedColumnDef } from "./useHydratedColumnDefs";

export type HydratedCell =
	| {
			type: "Text";
			header: string;
			value: string;
			error: string | undefined;
			update: (value: string) => void;
			disabled: boolean;
			visibleOnCard: boolean;
			recordId: string | undefined;
	  }
	| {
			type: "Reaction";
			header: string;
			value: Reaction[];
			error: string | undefined;
			toggleReaction: (user: User) => void;
			disabled: boolean;
			visibleOnCard: boolean;
	  }
	| {
			type: "Select";
			header: string;
			value: string;
			typeInfo?: { options: { label: string; color?: ColorVariant }[] };
			error: string | undefined;
			update: (value: string | null | typeof CLEAR_SELECT_ITEM_VALUE) => void;
			disabled: boolean;
			visibleOnCard: boolean;
	  }
	| {
			type: "Person";
			header: string;
			value: User | undefined;
			error: string | undefined;
			update: (value: User | null) => void;
			disabled: boolean;
			visibleOnCard: boolean;
	  };

export const hydrateCell = (
	row: unknown,
	{ columnDef, displayOptions }: HydratedColumnDef,
	store: Store<DatabaseTables> | null,
	linkedRecords: Record<string, string>,
	query: TopLevelQuerySpec,
	canEditTable: (string) => boolean,
	_disabled?: boolean
): HydratedCell => {
	const header = columnDef.header ?? columnDef.name;
	const accessorKey: JsonPathLiteral = `$.${columnDef.name}`;
	const unknownValue = pluck(row, accessorKey);
	const table = displayOptions.linkedTable ?? linkedRecords[accessorKey] ?? query.spec[0].table;
	const path = jsonPathToMergePatchPath(accessorKey);
	const canEdit = canEditTable(table);
	const visibleOnCard = displayOptions?.visibleOnCard ?? true;

	// FIXME: We need a notion of a primary key, lol.
	const recordId: string | undefined = row?.["id"];
	const disabled = _disabled || !recordId || displayOptions?.readonly || !canEdit || false;

	const typeInfo = columnDef.typeInfo;
	switch (typeInfo?.enrichedType) {
		case undefined:
		case "Text": {
			const { value, error } = stringifyValue(unknownValue);
			return {
				type: "Text",
				header,
				value,
				error,
				update: (value) => {
					if (!recordId) {
						throw new Error("Record ID is required");
					}
					void store?.applyMutation(table, {
						type: "UPDATE",
						recordId,
						change: {
							op: pluck(row, accessorKey) === undefined ? "add" : "replace",
							path,
							value,
						},
					});
				},
				disabled,
				visibleOnCard,
				recordId,
			};
		}
		case "Reaction": {
			const reactions = z.array(reactionSchema).safeParse(unknownValue);
			const value = reactions.success ? reactions.data : [];
			const error = reactions.success ? undefined : "Invalid reactions";
			return {
				type: "Reaction",
				header,
				value,
				error,
				toggleReaction: (user) => {
					const reactionRecordId = crypto.randomUUID();
					const reaction = value.find((r) => r.user.id === user.id);
					if (reaction) {
						void store?.delete(table, reaction.id);
					} else {
						if (!recordId) {
							throw new Error("Record ID is required");
						}
						void store?.insert(table, reactionRecordId, {
							id: reactionRecordId,
							linkedRecordId: recordId,
							user,
							createdAt: Date.now(),
						} satisfies Reaction);
					}
				},
				disabled,
				visibleOnCard,
			};
		}
		case "Select": {
			const { value, error } = stringifyValue(unknownValue);
			return {
				type: "Select",
				header,
				value,
				typeInfo: { options: typeInfo?.options ?? [] },
				error,
				update: (_value: string | null | typeof CLEAR_SELECT_ITEM_VALUE) => {
					const value =
						_value === CLEAR_SELECT_ITEM_VALUE ? null : _value === "" ? null : _value;

					if (!recordId) {
						throw new Error("Record ID is required");
					}
					const op = pluck(row, accessorKey) === undefined ? "add" : "replace";
					void store?.applyMutation(table, {
						type: "UPDATE",
						recordId,
						change: { op, path, value },
					});
				},
				disabled,
				visibleOnCard,
			};
		}
		case "Person": {
			let user: ZodSafeParseResult<User>;
			try {
				user = userSchema.safeParse(unknownValue);
			} catch (error) {
				console.error("Invalid user", error);
				user = { success: false, error: error as ZodError<User> };
			}
			const value = user.success ? user.data : undefined;
			const error = user.success ? undefined : "Invalid user";
			return {
				type: "Person",
				header,
				value,
				error,
				update: (user: User | null) => {
					if (!recordId) {
						throw new Error("Record ID is required");
					}
					const op = pluck(row, accessorKey) === undefined ? "add" : "replace";
					void store?.applyMutation(table, {
						type: "UPDATE",
						recordId,
						change: { op, path, value: user },
					});
				},
				disabled,
				visibleOnCard,
			};
		}
		default: {
			const exhaustive: never = typeInfo;
			throw new Error(`Unknown column type: ${(exhaustive as TypeInfo)?.enrichedType}`);
		}
	}
};
